/**
 * Location service for managing user location preferences
 * Handles zip code validation, storage, and radius settings
 */

const LOCATION_STORAGE_KEY = 'pawfect_match_location';

/**
 * Validate US zip code format
 */
export const validateZipCode = (zipCode) => {
  if (!zipCode) return false;
  
  // US zip code patterns: 12345 or 12345-6789
  const zipRegex = /^\d{5}(-\d{4})?$/;
  return zipRegex.test(zipCode.trim());
};

/**
 * Validate search radius
 */
export const validateRadius = (radius) => {
  const numRadius = parseInt(radius);
  return numRadius >= 1 && numRadius <= 500;
};

/**
 * Save location preferences to localStorage
 */
export const saveLocationPreferences = (location) => {
  try {
    const locationData = {
      zipCode: location.zipCode?.trim(),
      city: location.city?.trim(),
      state: location.state?.trim(),
      radius: parseInt(location.radius) || 25,
      timestamp: new Date().toISOString(),
    };

    localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(locationData));
    return locationData;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to save location preferences:', error);
    return null;
  }
};

/**
 * Load location preferences from localStorage
 */
export const loadLocationPreferences = () => {
  try {
    const stored = localStorage.getItem(LOCATION_STORAGE_KEY);
    if (!stored) return null;

    const locationData = JSON.parse(stored);
    
    // Validate stored data
    if (locationData.zipCode && !validateZipCode(locationData.zipCode)) {
      // Invalid zip code, clear storage
      clearLocationPreferences();
      return null;
    }

    return locationData;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to load location preferences:', error);
    return null;
  }
};

/**
 * Clear location preferences
 */
export const clearLocationPreferences = () => {
  try {
    localStorage.removeItem(LOCATION_STORAGE_KEY);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to clear location preferences:', error);
  }
};

/**
 * Get default location settings
 */
export const getDefaultLocation = () => ({
  zipCode: '',
  city: '',
  state: '',
  radius: 25,
});

/**
 * Format location for display
 */
export const formatLocationDisplay = (location) => {
  if (!location) return '';
  
  const parts = [];
  if (location.city) parts.push(location.city);
  if (location.state) parts.push(location.state);
  if (location.zipCode) parts.push(location.zipCode);
  
  const locationStr = parts.join(', ');
  const radiusStr = `within ${location.radius} miles`;
  
  return locationStr ? `${locationStr} (${radiusStr})` : radiusStr;
};

/**
 * Common US states for dropdown
 */
export const US_STATES = [
  { value: '', label: 'Select State' },
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

/**
 * Common search radius options
 */
export const RADIUS_OPTIONS = [
  { value: 5, label: '5 miles' },
  { value: 10, label: '10 miles' },
  { value: 15, label: '15 miles' },
  { value: 25, label: '25 miles' },
  { value: 50, label: '50 miles' },
  { value: 75, label: '75 miles' },
  { value: 100, label: '100 miles' },
  { value: 200, label: '200 miles' },
];